import { ApiClient } from '@/config/axios.config';
import type {
  CargoItem,
  ItineraryItem,
  CreateItineraryRequest,
  UpdateItineraryRequest,
} from '@/types/cargoList.types';
import { mockCargoData } from '@/data/mockCargoData';
import { environment } from '@/config/environment';

// Mock itinerary data for development
const mockItineraryData: Record<number, ItineraryItem[]> = {
  39800: [
    {
      purpose: 'Loading',
      port: 'Basrah Oil Terminal',
      via: 'Not Specified',
      arrivalTime: '2025-11-16T00:00:00',
      departureTime: '2025-11-19T00:00:00',
      speed: 12.0,
      note: '',
      order: 1,
      purposeId: 14,
      portId: 1171,
      viaId: 1,
      typeId: 3,
    },
    {
      purpose: 'Discharging',
      port: 'Visakhapatnam',
      via: 'Not Specified',
      arrivalTime: '2025-11-30T02:30:00',
      departureTime: '2025-12-04T02:30:00',
      speed: 12.0,
      note: '',
      order: 2,
      purposeId: 5,
      portId: 769,
      viaId: 1,
      typeId: 3,
    },
  ],
  // Add more mock itineraries for other cargo items
  39801: [
    {
      purpose: 'Loading',
      port: 'Ras Tanura',
      via: 'Not Specified',
      arrivalTime: '2025-11-18T00:00:00',
      departureTime: '2025-11-20T00:00:00',
      speed: 13.0,
      note: '',
      order: 1,
      purposeId: 14,
      portId: 1234,
      viaId: 1,
      typeId: 3,
    },
    {
      purpose: 'Discharging',
      port: 'Ulsan',
      via: 'Not Specified',
      arrivalTime: '2025-12-01T00:00:00',
      departureTime: '2025-12-03T00:00:00',
      speed: 13.0,
      note: '',
      order: 2,
      purposeId: 5,
      portId: 5678,
      viaId: 1,
      typeId: 3,
    },
  ],
};

export class CargoDetailsService {
  /**
   * Fetch a specific cargo item by ID
   */
  static async getCargoById(id: number): Promise<CargoItem> {
    try {
      return await ApiClient.get<CargoItem>(`/cargolists?id=${id}`);
    } catch (error) {
      // In development mode, fall back to mock data if API fails
      if (!environment.production) {
        console.warn('API failed for cargo details, using mock data:', error);
        const mockCargo = mockCargoData.find((item) => item.id === id);
        if (mockCargo) {
          return mockCargo;
        }
      }
      throw error;
    }
  }

  /**
   * Fetch itinerary for a specific cargo
   */
  static async getCargoItinerary(id: number): Promise<ItineraryItem[]> {
    try {
      return await ApiClient.get<ItineraryItem[]>(`/cargolist/itineraries/${id}`);
    } catch (error) {
      // In development mode, fall back to mock data if API fails
      if (!environment.production) {
        console.warn('API failed for cargo itinerary, using mock data:', error);
        return mockItineraryData[id] || [];
      }
      throw error;
    }
  }

  /**
   * Create a new itinerary item for a cargo. Responds 201 with an empty body.
   */
  static async createItineraryItem(data: CreateItineraryRequest): Promise<void> {
    return ApiClient.post<void>('/cargolist/itineraries', data);
  }

  /**
   * Update an existing itinerary item for a cargo. Responds 204 No Content, so
   * callers refetch the itinerary rather than merging a returned entity.
   */
  static async updateItineraryItem(
    typeId: number,
    fixtureId: number,
    previousOrder: number,
    data: UpdateItineraryRequest
  ): Promise<void> {
    const url = `/cargolist/itineraries/type/${typeId}/fixture/${fixtureId}/order/${previousOrder}`;
    return ApiClient.put<void>(url, data);
  }

  /**
   * Reorder itinerary items
   */
  static async reorderItineraryItems(
    fixtureId: number,
    typeId: number,
    oldOrder: number,
    newOrder: number
  ): Promise<void> {
    const url = `/cargolist/itinerary/order?fixtureId=${fixtureId}&typeId=${typeId}&old=${oldOrder}&new=${newOrder}`;
    await ApiClient.post(url);
  }
}
